import { X, Minus, Plus, ShoppingBag, ExternalLink } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

const ROZE_URL = "https://restoransroze.lv";

const Cart = ({
  isOpen,
  onClose,
  cart,
  language,
  updateQuantity,
  removeFromCart,
  getCartTotal,
  deliveryConfig,
  onCheckout,
  ordersDisabled = false,
}) => {
  const translations = {
    cart: {
      en: "Your Cart",
      ru: "Ваша корзина",
      lv: "Jūsu grozs",
    },
    empty: {
      en: "Your cart is empty",
      ru: "Ваша корзина пуста",
      lv: "Jūsu grozs ir tukšs",
    },
    subtotal: {
      en: "Subtotal",
      ru: "Промежуточный итог",
      lv: "Starpsumma",
    },
    total: {
      en: "Total",
      ru: "Итого",
      lv: "Kopā",
    },
    checkout: {
      en: "Proceed to Checkout",
      ru: "Оформить заказ",
      lv: "Turpināt pie norēķiniem",
    },
    ordersPaused: {
      en: "Online orders are currently paused. Please enjoy our pizzas at Restaurant Roze.",
      ru: "Онлайн-заказы временно недоступны. Приглашаем отведать наши пиццы в ресторане Roze.",
      lv: "Tiešsaistes pasūtījumi šobrīd nav pieejami. Aicinām baudīt mūsu pizzas restorānā Roze.",
    },
    goToRoze: {
      en: "Go to restoransroze.lv",
      ru: "Перейти на restoransroze.lv",
      lv: "Atvērt restoransroze.lv",
    },
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg bg-[#FDFBF7] text-[#1C1917] font-body border-l border-[#E7E5E4] p-0 flex flex-col" data-testid="cart-sheet">
        <SheetHeader className="px-6 sm:px-8 pt-8 pb-6 border-b border-[#E7E5E4]">
          <p className="rz-eyebrow m-0 mb-2">Roze di Napoli</p>
          <SheetTitle className="font-display text-[2rem] leading-none text-[#1C1917] text-left" data-testid="cart-title">{translations.cart[language]}</SheetTitle>
        </SheetHeader>

        <div className="flex-1 flex flex-col min-h-0">
          {cart.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-[#78716C] px-8 text-center" data-testid="empty-cart">
              <ShoppingBag className="w-12 h-12 mb-5 opacity-40" strokeWidth={1} />
              <p className="font-display text-xl text-[#44403C]">{translations.empty[language]}</p>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-2 divide-y divide-[#E7E5E4]" data-testid="cart-items">
                {cart.map((item) => (
                  <div
                    key={item.id}
                    className="py-5 flex items-start gap-4"
                    data-testid={`cart-item-${item.id}`}
                  >
                    <img
                      src={item.image}
                      alt={item[`name_${language}`]}
                      className="w-20 h-20 object-cover shrink-0 bg-[#F5F2EC]"
                      data-testid={`cart-item-image-${item.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <h4 className="font-display text-[1.2rem] leading-tight text-[#1C1917] m-0" data-testid={`cart-item-name-${item.id}`}>
                          {item[`name_${language}`]}
                        </h4>
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="min-w-[44px] min-h-[44px] -mt-2 -mr-3 inline-flex items-center justify-center text-[#A8A29E] hover:text-[#C2410C] transition-colors"
                          aria-label="Remove"
                          data-testid={`remove-item-${item.id}`}
                        >
                          <X className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      </div>
                      <p className="text-[0.95rem] text-[#78716C] m-0 mt-1 tabular-nums" data-testid={`cart-item-price-${item.id}`}>€{item.price.toFixed(2)}</p>
                      <div className="flex items-center justify-between mt-3">
                        <div className="inline-flex items-center border border-[#E7E5E4]">
                          <button
                            className="min-w-[44px] min-h-[44px] inline-flex items-center justify-center hover:bg-[#F5F2EC] transition-colors"
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            aria-label="Decrease"
                            data-testid={`decrease-quantity-${item.id}`}
                          >
                            <Minus className="w-4 h-4" strokeWidth={1.5} />
                          </button>
                          <span className="w-9 text-center font-medium tabular-nums" data-testid={`cart-item-quantity-${item.id}`}>{item.quantity}</span>
                          <button
                            className="min-w-[44px] min-h-[44px] inline-flex items-center justify-center hover:bg-[#F5F2EC] transition-colors"
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            aria-label="Increase"
                            data-testid={`increase-quantity-${item.id}`}
                          >
                            <Plus className="w-4 h-4" strokeWidth={1.5} />
                          </button>
                        </div>
                        <span className="font-semibold tabular-nums" data-testid={`cart-item-line-total-${item.id}`}>€{(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="px-6 sm:px-8 pt-5 pb-6 border-t border-[#E7E5E4] bg-[#FDFBF7] space-y-3">
                <div className="flex justify-between items-center text-[0.95rem] text-[#78716C]">
                  <span data-testid="cart-subtotal-label">{translations.subtotal[language]}</span>
                  <span className="tabular-nums" data-testid="cart-subtotal-amount">€{getCartTotal().toFixed(2)}</span>
                </div>

                <div className="flex justify-between items-baseline">
                  <span className="rz-eyebrow !text-[#1C1917]" data-testid="cart-total-label">{translations.total[language]}</span>
                  <span className="font-display text-[1.9rem] leading-none text-[#1C1917] tabular-nums" data-testid="cart-total-amount">€{getCartTotal().toFixed(2)}</span>
                </div>

                <p className="text-[0.72rem] text-[#A8A29E] m-0 pt-1">
                  {language === 'en' && `€${deliveryConfig.minimum_order_amount} minimum for delivery only. Pickup has no minimum.`}
                  {language === 'ru' && `€${deliveryConfig.minimum_order_amount} минимум только для доставки. Самовывоз без минимума.`}
                  {language === 'lv' && `€${deliveryConfig.minimum_order_amount} minimums tikai piegādei. Līdzņemšanai nav minimuma.`}
                </p>

                {ordersDisabled ? (
                  <div className="space-y-3 pt-1" data-testid="cart-orders-paused-block">
                    <p className="text-sm text-[#C2410C] border border-[#C2410C]/30 px-3 py-2 text-center m-0">
                      {translations.ordersPaused[language]}
                    </p>
                    <a
                      href={ROZE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rz-btn w-full"
                      data-testid="cart-roze-cta"
                    >
                      {translations.goToRoze[language]}
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                ) : (
                  <button
                    onClick={onCheckout}
                    className="rz-btn w-full mt-2"
                    data-testid="checkout-button"
                  >
                    {translations.checkout[language]}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default Cart;
